import React, { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import DOMPurify from "dompurify";
import { useAuth } from "../context/AuthContext";
import { employeesApi, usersApi, listsApi, analyticsApi, entraApi, notificationsApi, whatsNewApi } from "../services/api";

const AdminPage = () => {
  const { user, logout } = useAuth();
  const [activeTab, setActiveTab] = useState("analytics");
  const [loading, setLoading] = useState(true);

  // Data states
  const [analytics, setAnalytics] = useState(null);
  const [productivity, setProductivity] = useState(null);
  const [prodLoading, setProdLoading] = useState(false);
  const [prodStartDate, setProdStartDate] = useState(() => {
    const d = new Date(); d.setDate(1);
    return d.toISOString().split("T")[0];
  });
  const [prodEndDate, setProdEndDate] = useState(() => new Date().toISOString().split("T")[0]);
  const [processedEmployees, setProcessedEmployees] = useState([]);
  const [processedSearch, setProcessedSearch] = useState("");
  const [viewingProcessed, setViewingProcessed] = useState(null);  // employee object or null
  const [resendingTq, setResendingTq] = useState(null);  // employee id currently re-sending

  // What's New (announcements) state
  const [announcements, setAnnouncements] = useState([]);
  const [editingAnn, setEditingAnn] = useState(null); // existing record being edited
  const [annDraft, setAnnDraft] = useState({ title: "", body: "", active: true });
  const [annSaving, setAnnSaving] = useState(false);
  const [unprocessedEmployees, setUnprocessedEmployees] = useState([]);
  const [failedSubmissions, setFailedSubmissions] = useState([]);
  const [submissionLocked, setSubmissionLocked] = useState([]);
  const [users, setUsers] = useState([]);
  const [lists, setLists] = useState([]);
  const [selectedList, setSelectedList] = useState(null);
  const [lockedRecords, setLockedRecords] = useState([]);

  // Modal states
  const [showAddUser, setShowAddUser] = useState(false);
  const [showAddItem, setShowAddItem] = useState(false);
  const [showBulkAdd, setShowBulkAdd] = useState(false);
  const [newUserEmail, setNewUserEmail] = useState("");
  const [newUserRole, setNewUserRole] = useState("user");
  const [newItem, setNewItem] = useState("");
  const [newItemField1, setNewItemField1] = useState("");
  const [newItemField2, setNewItemField2] = useState("");
  const [bulkItems, setBulkItems] = useState("");

  // Status Changes (Entra ID) states
  const [entraSearchQuery, setEntraSearchQuery] = useState("");
  const [entraUser, setEntraUser] = useState(null);
  const [entraLoading, setEntraLoading] = useState(false);
  const [entraEditMode, setEntraEditMode] = useState(false);
  const [entraAdditionalEmails, setEntraAdditionalEmails] = useState([]);
  const [entraNewAdditionalEmail, setEntraNewAdditionalEmail] = useState("");
  const [entraEditData, setEntraEditData] = useState({
    accountEnabled: true,
    employeeType: "",
    jobTitle: "",
    officeLocation: "",
    businessPhones: [],
    mobilePhone: "",
    personalEmail: "",
    facilityName: "",
    managerEmail: "",
    startDate: ""
  });
  const [entraAuditLog, setEntraAuditLog] = useState([]);

  // Notification state
  const [notifRecipients, setNotifRecipients] = useState({ status_change_emails: [], termination_emails: [] });
  const [notifTemplates, setNotifTemplates] = useState({});
  const [notifNewEmail, setNotifNewEmail] = useState({ status_change: "", termination: "" });
  const [notifEditingTemplate, setNotifEditingTemplate] = useState(null); // "status_change" or "termination"
  const [notifTemplateEdit, setNotifTemplateEdit] = useState({ subject: "", html_body: "" });
  const [notifLoading, setNotifLoading] = useState(false);
  const [notifTestLoading, setNotifTestLoading] = useState(false);
  const [diagnoseResult, setDiagnoseResult] = useState(null);

  // Lists that have two columns (stored as "field1|field2")
  const twoColumnLists = {
    facility_list: { field1: "Facility ID", field2: "Facility Name" },
    job_titles: { field1: "Code", field2: "Description" },
    company_codes: { field1: "ADP Code", field2: "Com Code" },
  };

  const isTwoColumnList = (listName) => listName in twoColumnLists;
  const getListFields = (listName) => twoColumnLists[listName] || { field1: "Value 1", field2: "Value 2" };

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [analyticsData, processedData, unprocessedData, failedData, submissionLockedData, usersData, listsData, locksData] = await Promise.all([
        analyticsApi.getOverview(),
        employeesApi.getProcessed(),
        employeesApi.getUnprocessed(),
        employeesApi.getFailedSubmissions().catch(() => []),
        employeesApi.getSubmissionLocked().catch(() => []),
        usersApi.getAll(),
        listsApi.getAll(),
        employeesApi.getAdminLocks().catch(() => []),
      ]);
      setAnalytics(analyticsData);
      setProcessedEmployees(processedData);
      setUnprocessedEmployees(unprocessedData);
      setFailedSubmissions(failedData);
      setSubmissionLocked(submissionLockedData);
      setUsers(usersData);
      setLists(listsData);
      setLockedRecords(locksData);
    } catch (err) {
      toast.error("Failed to load admin data");
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Admin unlock a record
  const handleAdminUnlock = async (employeeId, lockedBy) => {
    if (!window.confirm(`Are you sure you want to unlock this record? This will force ${lockedBy} out of their editing session.`)) return;
    try {
      await employeesApi.adminUnlock(employeeId);
      toast.success("Record unlocked successfully");
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to unlock record");
    }
  };

  // User management
  const handleAddUser = async () => {
    if (!newUserEmail) {
      toast.error("Please enter an email address");
      return;
    }
    try {
      await usersApi.create(newUserEmail, newUserRole);
      toast.success("User added successfully");
      setShowAddUser(false);
      setNewUserEmail("");
      setNewUserRole("user");
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to add user");
    }
  };

  const handleToggleUserStatus = async (userId, currentStatus) => {
    try {
      await usersApi.update(userId, { is_active: !currentStatus });
      toast.success("User status updated");
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update user");
    }
  };

  const handleChangeUserRole = async (userId, newRole) => {
    try {
      await usersApi.update(userId, { role: newRole });
      toast.success("User role updated");
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update role");
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm("Are you sure you want to delete this user?")) return;
    try {
      await usersApi.delete(userId);
      toast.success("User deleted");
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to delete user");
    }
  };

  // List management - Single item
  const handleAddListItem = async () => {
    const listName = selectedList?.name;
    let itemValue = "";
    
    if (isTwoColumnList(listName)) {
      if (!newItemField1 || !newItemField2) {
        toast.error("Please fill in both fields");
        return;
      }
      itemValue = `${newItemField1.trim()}|${newItemField2.trim()}`;
    } else {
      if (!newItem) {
        toast.error("Please enter an item");
        return;
      }
      itemValue = newItem.trim();
    }
    
    try {
      await listsApi.addItem(selectedList.name, itemValue);
      toast.success("Item added");
      setNewItem("");
      setNewItemField1("");
      setNewItemField2("");
      setShowAddItem(false);
      fetchData();
      const updated = await listsApi.get(selectedList.name);
      setSelectedList(updated);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to add item");
    }
  };

  // List management - Bulk add
  const handleBulkAddItems = async () => {
    if (!bulkItems || !selectedList) {
      toast.error("Please enter items");
      return;
    }

    const listName = selectedList.name;
    let items = [];
    
    if (isTwoColumnList(listName)) {
      // For two-column lists, expect "value1|value2" or "value1,value2" or "value1\tvalue2" per line
      const lines = bulkItems.split(/\n/).filter((line) => line.trim().length > 0);
      for (const line of lines) {
        // Try to split by pipe, tab, or comma (in that order)
        let parts;
        if (line.includes("|")) {
          parts = line.split("|");
        } else if (line.includes("\t")) {
          parts = line.split("\t");
        } else if (line.includes(",")) {
          parts = line.split(",");
        } else {
          continue; // Skip lines without separator
        }
        if (parts.length >= 2) {
          items.push(`${parts[0].trim()}|${parts[1].trim()}`);
        }
      }
    } else {
      // Split by newlines, commas, or tabs
      items = bulkItems
        .split(/[\n,\t]+/)
        .map((item) => item.trim())
        .filter((item) => item.length > 0);
    }

    if (items.length === 0) {
      toast.error("No valid items found");
      return;
    }

    let added = 0;
    let skipped = 0;

    for (const item of items) {
      try {
        await listsApi.addItem(selectedList.name, item);
        added++;
      } catch (err) {
        // Item might already exist
        skipped++;
      }
    }

    toast.success(`Added ${added} items${skipped > 0 ? `, ${skipped} skipped (duplicates)` : ""}`);
    setBulkItems("");
    setShowBulkAdd(false);
    fetchData();
    const updated = await listsApi.get(selectedList.name);
    setSelectedList(updated);
  };

  const handleRemoveListItem = async (listName, item) => {
    if (!window.confirm(`Remove "${item}" from the list?`)) return;
    try {
      await listsApi.removeItem(listName, item);
      toast.success("Item removed");
      fetchData();
      if (selectedList?.name === listName) {
        const updated = await listsApi.get(listName);
        setSelectedList(updated);
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to remove item");
    }
  };

  // Unprocess employee
  const handleUnprocess = async (employeeId) => {
    if (!window.confirm("Return this employee to the unprocessed queue?")) return;
    try {
      await employeesApi.unprocess(employeeId);
      toast.success("Employee marked as unprocessed");
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to unprocess employee");
    }
  };

  // Re-send a processed employee to TherapyQuest
  const handleResendTherapyQuest = async (employeeId) => {
    setResendingTq(employeeId);
    try {
      const res = await employeesApi.resendTherapyQuest(employeeId);
      if (res.success) {
        toast.success("Sent to TherapyQuest");
      } else {
        toast.error(res.message || "TherapyQuest send failed");
      }
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to send to TherapyQuest");
    } finally {
      setResendingTq(null);
    }
  };

  // Delete unprocessed employee
  const handleDeleteEmployee = async (employeeId, employeeName) => {
    if (!window.confirm(`Are you sure you want to permanently delete ${employeeName}? This action cannot be undone.`)) return;
    try {
      await employeesApi.delete(employeeId);
      toast.success("Employee deleted successfully");
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to delete employee");
    }
  };

  // Retry failed submission
  const handleRetrySubmission = async (employeeId, employeeName) => {
    if (!window.confirm(`Clear the error for ${employeeName} and allow them to be resubmitted?`)) return;
    try {
      await employeesApi.retrySubmission(employeeId);
      toast.success("Error cleared. Employee can be resubmitted.");
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to clear error");
    }
  };

  // Force unlock submission-locked record
  const handleForceUnlockSubmission = async (employeeId, employeeName) => {
    if (!window.confirm(`Force unlock ${employeeName}? This will move them to Failed Submissions for retry. Use this if Power Automate never responded.`)) return;
    try {
      await employeesApi.forceUnlockSubmission(employeeId);
      toast.success("Submission lock cleared. Employee moved to Failed Submissions.");
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to unlock");
    }
  };

  // Entra ID Status Changes handlers
  const handleEntraSearch = async () => {
    if (!entraSearchQuery.trim()) {
      toast.error("Please enter an Employee ID");
      return;
    }
    setEntraLoading(true);
    setEntraUser(null);
    setEntraEditMode(false);
    try {
      const user = await entraApi.searchUser(entraSearchQuery.trim());
      setEntraUser(user);
      setEntraEditData({
        accountEnabled: user.accountEnabled ?? true,
        employeeType: user.employeeType || "",
        jobTitle: user.jobTitle || "",
        officeLocation: user.officeLocation || "",
        businessPhones: user.businessPhones || [],
        mobilePhone: user.mobilePhone || "",
        personalEmail: user.personalEmail || "",
        facilityName: user.facilityName || "",
        managerEmail: user.managerEmail || "",
        startDate: user.startDate || ""
      });
    } catch (err) {
      if (err.response?.status === 404) {
        toast.error(`User not found with Employee ID: ${entraSearchQuery}`);
      } else {
        toast.error(err.response?.data?.detail || "Failed to search Entra ID");
      }
    } finally {
      setEntraLoading(false);
    }
  };

  const handleEntraUpdate = async () => {
    if (!entraUser) return;
    
    // Build update payload - only send changed fields
    const updates = {};
    if (entraEditData.accountEnabled !== entraUser.accountEnabled) {
      updates.accountEnabled = entraEditData.accountEnabled;
      updates.previousAccountEnabled = entraUser.accountEnabled;
    }
    if (entraEditData.employeeType !== (entraUser.employeeType || "")) {
      updates.employeeType = entraEditData.employeeType || null;
      // Include previous employee type for license removal logic
      updates.previousEmployeeType = entraUser.employeeType || null;
    }
    // Always include displayName for notification emails
    updates.displayName = entraUser.displayName || null;
    if (entraEditData.jobTitle !== (entraUser.jobTitle || "")) {
      updates.jobTitle = entraEditData.jobTitle || null;
    }
    if (entraEditData.officeLocation !== (entraUser.officeLocation || "")) {
      updates.officeLocation = entraEditData.officeLocation || null;
    }
    // Compare business phone (first element of array)
    const currentBusinessPhone = (entraUser.businessPhones && entraUser.businessPhones[0]) || "";
    const newBusinessPhone = (entraEditData.businessPhones && entraEditData.businessPhones[0]) || "";
    if (newBusinessPhone !== currentBusinessPhone) {
      updates.businessPhones = newBusinessPhone ? [newBusinessPhone] : [];
    }
    if (entraEditData.mobilePhone !== (entraUser.mobilePhone || "")) {
      updates.mobilePhone = entraEditData.mobilePhone || null;
    }
    if (entraEditData.personalEmail !== (entraUser.personalEmail || "")) {
      updates.personalEmail = entraEditData.personalEmail || null;
    }
    if (entraEditData.facilityName !== (entraUser.facilityName || "")) {
      updates.facilityName = entraEditData.facilityName || null;
    }
    if (entraEditData.managerEmail !== (entraUser.managerEmail || "")) {
      updates.managerEmail = entraEditData.managerEmail || null;
    }
    if (entraEditData.startDate !== (entraUser.startDate || "")) {
      updates.startDate = entraEditData.startDate || null;
    }

    if (Object.keys(updates).filter(k => k !== "displayName" && k !== "previousAccountEnabled" && k !== "previousEmployeeType").length === 0) {
      toast.info("No changes to save");
      return;
    }

    // Include additional notification recipients (auto-include any typed-but-not-yet-added emails)
    const allAdditionalEmails = [...entraAdditionalEmails];
    if (entraNewAdditionalEmail.trim()) {
      const pendingEmails = entraNewAdditionalEmail.split(",").map(e => e.trim()).filter(e => e && e.includes("@"));
      pendingEmails.forEach(e => { if (!allAdditionalEmails.includes(e)) allAdditionalEmails.push(e); });
    }
    if (allAdditionalEmails.length > 0) {
      updates.additionalNotifyEmails = allAdditionalEmails;
    }

    // Warn if changing from FT to PT/PRN/Contractor (will remove licenses)
    const prevType = (entraUser.employeeType || "").toUpperCase();
    const newType = (entraEditData.employeeType || "").toUpperCase();
    if (prevType === "FT" && ["PT", "PRN", "CONTRACTOR"].includes(newType)) {
      const confirmed = window.confirm(
        `⚠️ WARNING: Changing from FT to ${newType} will automatically remove all email and Office licenses from this user.\n\nDo you want to continue?`
      );
      if (!confirmed) {
        return;
      }
    }

    setEntraLoading(true);
    try {
      const result = await entraApi.updateUser(entraUser.id, updates);
      
      // Show appropriate success message
      if (result.license_removal && result.license_removal.removed > 0) {
        toast.success(`User updated and ${result.license_removal.removed} license(s) removed`);
      } else {
        toast.success("User updated successfully in Entra ID");
      }
      
      // Refresh user data
      const updatedUser = await entraApi.searchUser(entraSearchQuery.trim());
      setEntraUser(updatedUser);
      setEntraEditData({
        accountEnabled: updatedUser.accountEnabled ?? true,
        employeeType: updatedUser.employeeType || "",
        jobTitle: updatedUser.jobTitle || "",
        officeLocation: updatedUser.officeLocation || "",
        businessPhones: updatedUser.businessPhones || [],
        mobilePhone: updatedUser.mobilePhone || "",
        personalEmail: updatedUser.personalEmail || "",
        facilityName: updatedUser.facilityName || "",
        managerEmail: updatedUser.managerEmail || "",
        startDate: updatedUser.startDate || ""
      });
      setEntraEditMode(false);
      setEntraAdditionalEmails([]);
      setEntraNewAdditionalEmail("");
      // Refresh audit log
      fetchEntraAuditLog();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update user in Entra ID");
    } finally {
      setEntraLoading(false);
    }
  };

  const handleEntraCancelEdit = () => {
    if (entraUser) {
      setEntraEditData({
        accountEnabled: entraUser.accountEnabled ?? true,
        employeeType: entraUser.employeeType || "",
        jobTitle: entraUser.jobTitle || "",
        officeLocation: entraUser.officeLocation || "",
        businessPhones: entraUser.businessPhones || [],
        mobilePhone: entraUser.mobilePhone || "",
        personalEmail: entraUser.personalEmail || "",
        facilityName: entraUser.facilityName || "",
        managerEmail: entraUser.managerEmail || "",
        startDate: entraUser.startDate || ""
      });
    }
    setEntraEditMode(false);
    setEntraAdditionalEmails([]);
    setEntraNewAdditionalEmail("");
  };

  const fetchEntraAuditLog = async () => {
    try {
      const logs = await entraApi.getAuditLog(100);
      setEntraAuditLog(logs);
    } catch (err) {
      console.error("Failed to fetch audit log", err);
    }
  };

  // Fetch audit log when switching to the audit tab
  useEffect(() => {
    if (activeTab === "entraAudit") {
      fetchEntraAuditLog();
    }
    if (activeTab === "notifications") {
      fetchNotifications();
    }
    if (activeTab === "analytics") {
      fetchProductivity();
    }
    if (activeTab === "whatsNew") {
      fetchAnnouncements();
    }
  }, [activeTab]);

  const fetchAnnouncements = async () => {
    try {
      const list = await whatsNewApi.adminListAll();
      setAnnouncements(Array.isArray(list) ? list : []);
    } catch (err) {
      console.error("Failed to load announcements:", err);
      toast.error("Could not load announcements");
    }
  };

  const handleEditAnnouncement = (ann) => {
    setEditingAnn(ann);
    setAnnDraft({ title: ann.title || "", body: ann.body || "", active: !!ann.active });
  };

  const handleNewAnnouncement = () => {
    setEditingAnn(null);
    setAnnDraft({ title: "", body: "", active: true });
  };

  const handleSaveAnnouncement = async () => {
    if (!annDraft.title.trim() || !annDraft.body.trim()) {
      toast.error("Title and body are required");
      return;
    }
    setAnnSaving(true);
    try {
      if (editingAnn) {
        await whatsNewApi.adminUpdate(editingAnn.id, annDraft);
        toast.success("Announcement updated");
      } else {
        await whatsNewApi.adminCreate(annDraft);
        toast.success("Announcement published");
      }
      setEditingAnn(null);
      setAnnDraft({ title: "", body: "", active: true });
      await fetchAnnouncements();
    } catch (err) {
      console.error("Save announcement failed:", err);
      toast.error(err.response?.data?.detail || "Failed to save announcement");
    } finally {
      setAnnSaving(false);
    }
  };

  const handleDeactivateAnnouncement = async (ann) => {
    if (!window.confirm(`Deactivate "${ann.title}"? It will be hidden from users but kept in the system.`)) return;
    try {
      await whatsNewApi.adminDeactivate(ann.id);
      toast.success("Announcement deactivated");
      await fetchAnnouncements();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to deactivate");
    }
  };

  const handleReactivateAnnouncement = async (ann) => {
    try {
      await whatsNewApi.adminUpdate(ann.id, { active: true });
      toast.success("Announcement reactivated");
      await fetchAnnouncements();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to reactivate");
    }
  };

  const fetchProductivity = async () => {
    setProdLoading(true);
    try {
      const data = await analyticsApi.getProductivity(prodStartDate, prodEndDate);
      setProductivity(data);
    } catch (err) {
      console.error("Failed to load productivity data:", err);
    } finally {
      setProdLoading(false);
    }
  };

  const handleDownloadPdf = async () => {
    try {
      toast.info("Generating PDF report...");
      const blob = await analyticsApi.downloadReportPdf(prodStartDate, prodEndDate);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `HR_Productivity_Report_${prodStartDate}_${prodEndDate}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("PDF report downloaded");
    } catch (err) {
      toast.error("Failed to generate PDF report");
      console.error(err);
    }
  };

  const fetchNotifications = async () => {
    setNotifLoading(true);
    try {
      const [recipients, templates] = await Promise.all([
        notificationsApi.getRecipients(),
        notificationsApi.getTemplates()
      ]);
      setNotifRecipients(recipients);
      setNotifTemplates(templates);
    } catch (err) {
      console.error("Failed to fetch notifications", err);
      toast.error("Failed to load notification settings");
    } finally {
      setNotifLoading(false);
    }
  };

  const handleAddNotifEmail = async (type) => {
    const email = notifNewEmail[type].trim();
    if (!email || !email.includes("@")) {
      toast.error("Please enter a valid email address");
      return;
    }
    const key = type === "status_change" ? "status_change_emails" : "termination_emails";
    if (notifRecipients[key].includes(email)) {
      toast.error("Email already in list");
      return;
    }
    const updated = { ...notifRecipients, [key]: [...notifRecipients[key], email] };
    try {
      await notificationsApi.updateRecipients(updated);
      setNotifRecipients(updated);
      setNotifNewEmail(prev => ({ ...prev, [type]: "" }));
      toast.success("Recipient added");
    } catch (err) {
      const detail = err.response?.data?.detail || err.message || "Unknown error";
      toast.error(`Failed to add recipient: ${detail}`);
    }
  };

  const handleRemoveNotifEmail = async (type, email) => {
    const key = type === "status_change" ? "status_change_emails" : "termination_emails";
    const updated = { ...notifRecipients, [key]: notifRecipients[key].filter(e => e !== email) };
    try {
      await notificationsApi.updateRecipients(updated);
      setNotifRecipients(updated);
      toast.success("Recipient removed");
    } catch (err) {
      const detail = err.response?.data?.detail || err.message || "Unknown error";
      toast.error(`Failed to remove recipient: ${detail}`);
    }
  };

  const handleEditTemplate = (type) => {
    const template = notifTemplates[type] || { subject: "", html_body: "" };
    setNotifTemplateEdit({ subject: template.subject, html_body: template.html_body });
    setNotifEditingTemplate(type);
  };

  const handleSaveTemplate = async () => {
    if (!notifEditingTemplate) return;
    try {
      await notificationsApi.updateTemplate(notifEditingTemplate, {
        template_type: notifEditingTemplate,
        subject: notifTemplateEdit.subject,
        html_body: notifTemplateEdit.html_body
      });
      setNotifTemplates(prev => ({ ...prev, [notifEditingTemplate]: { ...notifTemplateEdit } }));
      setNotifEditingTemplate(null);
      toast.success("Template saved");
    } catch (err) {
      const detail = err.response?.data?.detail || err.message || "Unknown error";
      console.error("Template save error:", err.response?.status, detail);
      toast.error(`Failed to save template: ${detail}`);
    }
  };

  const handleSendTestEmail = async () => {
    setNotifTestLoading(true);
    try {
      const result = await notificationsApi.sendTest();
      toast.success(result.message);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to send test email");
    } finally {
      setNotifTestLoading(false);
    }
  };

  // Helper to get job titles from lists
  const getJobTitles = () => {
    const jobTitlesList = lists.find(l => l.name === "job_titles");
    if (!jobTitlesList) return [];
    return jobTitlesList.items.map(item => {
      if (item.includes("|")) {
        const parts = item.split("|");
        return { code: parts[0], description: parts[1], full: item };
      }
      return { code: item, description: item, full: item };
    });
  };

  // Helper to get facilities from lists
  const getFacilities = () => {
    const facilityList = lists.find(l => l.name === "facility_list");
    if (!facilityList) return [];
    return facilityList.items.map(item => {
      if (item.includes("|")) {
        const parts = item.split("|");
        return { id: parts[0], name: parts[1], full: item };
      }
      return { id: item, name: item, full: item };
    });
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-spinner"></div>
        <p>Loading admin panel...</p>
      </div>
    );
  }

  return (
    <div className="app-layout">
      <header className="app-header">
        <div className="header-left">
          <div className="header-logo">
            <img src="/tmc-onboarding-logo.png" alt="TMC Onboarding" style={{ height: "50px", width: "auto" }} />
          </div>
        </div>

        <nav className="header-nav">
          <Link to="/" className="nav-link">Process Employees</Link>
          <Link to="/admin" className="nav-link active">Admin Panel</Link>
        </nav>

        <div className="header-right">
          <div className="user-info">
            <div className="user-avatar">{user?.name?.charAt(0) || user?.email?.charAt(0) || "A"}</div>
            <div>
              <div className="user-name">{user?.name || user?.email}</div>
              <div className="user-role">Admin</div>
            </div>
          </div>
          <button className="logout-btn" onClick={logout}>Sign Out</button>
        </div>
      </header>

      <main className="main-content">
        {/* Tabs */}
        <div className="tabs">
          <button className={`tab ${activeTab === "analytics" ? "active" : ""}`} onClick={() => setActiveTab("analytics")} data-testid="tab-analytics">Analytics</button>
          <button className={`tab ${activeTab === "users" ? "active" : ""}`} onClick={() => setActiveTab("users")} data-testid="tab-users">Users</button>
          <button className={`tab ${activeTab === "unprocessed" ? "active" : ""}`} onClick={() => setActiveTab("unprocessed")} data-testid="tab-unprocessed">
            Unprocessed Employees {unprocessedEmployees.length > 0 && <span className="badge badge-warning" style={{ marginLeft: "0.5rem" }}>{unprocessedEmployees.length}</span>}
          </button>
          <button className={`tab ${activeTab === "pending" ? "active" : ""}`} onClick={() => setActiveTab("pending")} data-testid="tab-pending">
            Pending Submissions {submissionLocked.length > 0 && <span className="badge badge-info" style={{ marginLeft: "0.5rem" }}>{submissionLocked.length}</span>}
          </button>
          <button className={`tab ${activeTab === "failed" ? "active" : ""}`} onClick={() => setActiveTab("failed")} data-testid="tab-failed">
            Failed Submissions {failedSubmissions.length > 0 && <span className="badge badge-error" style={{ marginLeft: "0.5rem" }}>{failedSubmissions.length}</span>}
          </button>
          <button className={`tab ${activeTab === "processed" ? "active" : ""}`} onClick={() => setActiveTab("processed")} data-testid="tab-processed">Processed Employees</button>
          <button className={`tab ${activeTab === "lists" ? "active" : ""}`} onClick={() => setActiveTab("lists")} data-testid="tab-lists">Dropdown Lists</button>
          <button className={`tab ${activeTab === "locks" ? "active" : ""}`} onClick={() => setActiveTab("locks")} data-testid="tab-locks">
            Locked Records {lockedRecords.length > 0 && <span className="badge badge-warning" style={{ marginLeft: "0.5rem" }}>{lockedRecords.length}</span>}
          </button>
          <button className={`tab ${activeTab === "entraAudit" ? "active" : ""}`} onClick={() => setActiveTab("entraAudit")} data-testid="tab-entra-audit">
            Change History
          </button>
          <button className={`tab ${activeTab === "notifications" ? "active" : ""}`} onClick={() => setActiveTab("notifications")} data-testid="tab-notifications">
            Notifications
          </button>
          <button className={`tab ${activeTab === "whatsNew" ? "active" : ""}`} onClick={() => setActiveTab("whatsNew")} data-testid="tab-whats-new">
            What&apos;s New
          </button>
        </div>

        {/* Pending Submissions Tab */}
        {activeTab === "pending" && (
          <div className="card">
            <div className="card-header">
              <h2 className="card-title">⏳ Pending Submissions</h2>
              <span className="badge badge-info">{submissionLocked.length} pending</span>
            </div>
            <div className="card-body">
              {submissionLocked.length === 0 ? (
                <div className="empty-state"><p>No pending submissions. All submissions have been processed by Power Automate.</p></div>
              ) : (
                <div className="table-container">
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>Position ID</th>
                        <th>Name</th>
                        <th>HR Agent</th>
                        <th>Submitted At</th>
                        <th>Status</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {submissionLocked.map((emp) => (
                        <tr key={emp.id}>
                          <td>{emp.position_id || emp.id}</td>
                          <td><strong>{emp.first_name || "-"} {emp.last_name || "-"}</strong></td>
                          <td>{emp.hr_agent || "-"}</td>
                          <td>{emp.submitted_at ? new Date(emp.submitted_at).toLocaleString() : "-"}</td>
                          <td><span className="badge badge-info">Waiting for Power Automate</span></td>
                          <td>
                            <button 
                              className="btn btn-warning btn-sm" 
                              onClick={() => handleForceUnlockSubmission(emp.id, `${emp.first_name || ''} ${emp.last_name || ''}`.trim() || emp.position_id)}
                              data-testid={`force-unlock-${emp.id}`}
                            >
                              Force Unlock
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              <div style={{ marginTop: "1rem", padding: "1rem", background: "var(--slate-100)", borderRadius: "8px", fontSize: "0.9rem", color: "var(--slate-600)" }}>
                <strong>Note:</strong> Records here are waiting for Power Automate to call back with success/error. 
                Use <strong>Force Unlock</strong> only if Power Automate failed to respond and the record is stuck.
                Force-unlocked records will move to Failed Submissions for retry.
              </div>
            </div>
          </div>
        )}

        {/* Failed Submissions Tab */}
        {activeTab === "failed" && (
          <div className="card">
            <div className="card-header">
              <h2 className="card-title">❌ Failed Submissions</h2>
              <span className="badge badge-error">{failedSubmissions.length} failed</span>
            </div>
            <div className="card-body">
              {failedSubmissions.length === 0 ? (
                <div className="empty-state"><p>No failed submissions. All submissions were successful!</p></div>
              ) : (
                <div className="table-container">
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>Position ID</th>
                        <th>Name</th>
                        <th>HR Agent</th>
                        <th>Submitted At</th>
                        <th>Error Message</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {failedSubmissions.map((emp) => (
                        <tr key={emp.id}>
                          <td>{emp.position_id || emp.id}</td>
                          <td><strong>{emp.first_name || "-"} {emp.last_name || "-"}</strong></td>
                          <td>{emp.hr_agent || "-"}</td>
                          <td>{emp.submitted_at ? new Date(emp.submitted_at).toLocaleString() : "-"}</td>
                          <td style={{ maxWidth: "300px", wordBreak: "break-word", color: "var(--error)" }}>
                            {emp.submission_error || "Unknown error"}
                          </td>
                          <td>
                            <div style={{ display: "flex", gap: "0.5rem" }}>
                              <button 
                                className="btn btn-primary btn-sm" 
                                onClick={() => handleRetrySubmission(emp.id, `${emp.first_name || ''} ${emp.last_name || ''}`.trim() || emp.position_id)}
                                data-testid={`retry-${emp.id}`}
                              >
                                Clear Error
                              </button>
                              <button 
                                className="btn btn-danger btn-sm" 
                                onClick={() => handleDeleteEmployee(emp.id, `${emp.first_name || ''} ${emp.last_name || ''}`.trim() || emp.position_id)}
                                data-testid={`delete-failed-${emp.id}`}
                              >
                                Delete
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              <div style={{ marginTop: "1rem", padding: "1rem", background: "var(--slate-100)", borderRadius: "8px", fontSize: "0.9rem", color: "var(--slate-600)" }}>
                <strong>How it works:</strong>
                <ul style={{ margin: "0.5rem 0 0 1rem" }}>
                  <li><strong>Clear Error:</strong> Removes the error status so the employee can be resubmitted from the main processing page.</li>
                  <li><strong>Delete:</strong> Permanently removes the employee record.</li>
                  <li>Power Automate sends an email with detailed error information.</li>
                </ul>
              </div>
            </div>
          </div>
        )}

        {/* Locked Records Tab */}
        {activeTab === "locks" && (
          <div className="card">
            <div className="card-header">
              <h2 className="card-title">🔒 Currently Locked Records</h2>
            </div>
            <div className="card-body">
              {lockedRecords.length === 0 ? (
                <div className="empty-state">
                  <p>No records are currently locked.</p>
                </div>
              ) : (
                <div className="table-container">
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>Employee</th>
                        <th>Position ID</th>
                        <th>Locked By</th>
                        <th>Locked At</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {lockedRecords.map((lock) => {
                        const lockedAt = new Date(lock.locked_at);
                        const now = new Date();
                        const minutesAgo = Math.round((now - lockedAt) / 60000);
                        return (
                          <tr key={lock.employee_id}>
                            <td><strong>{lock.employee_name || "Unknown"}</strong></td>
                            <td>{lock.position_id || "-"}</td>
                            <td>{lock.locked_by}</td>
                            <td>
                              {minutesAgo < 1 ? "Just now" : 
                               minutesAgo < 60 ? `${minutesAgo} min ago` : 
                               `${Math.round(minutesAgo / 60)} hr ago`}
                            </td>
                            <td>
                              <button 
                                className="btn btn-danger btn-sm"
                                onClick={() => handleAdminUnlock(lock.employee_id, lock.locked_by)}
                              >
                                Force Unlock
                              </button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
              <div style={{ marginTop: "1rem", padding: "1rem", background: "var(--slate-100)", borderRadius: "8px", fontSize: "0.9rem", color: "var(--slate-600)" }}>
                <strong>Note:</strong> Locks automatically expire after 30 minutes of inactivity. Force unlocking will immediately release the lock, potentially causing the user to lose unsaved changes.
              </div>
            </div>
          </div>
        )}

        {/* Analytics Tab */}
        {activeTab === "analytics" && (
          <div>
            {/* Date Range Controls */}
            <div className="card" style={{ marginBottom: "1.5rem" }}>
              <div className="card-body" style={{ display: "flex", alignItems: "center", gap: "1rem", flexWrap: "wrap" }}>
                <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                  <label style={{ fontWeight: 600, fontSize: "0.9rem" }}>From:</label>
                  <input type="date" className="form-input" value={prodStartDate}
                    onChange={(e) => setProdStartDate(e.target.value)}
                    style={{ width: "auto" }} data-testid="analytics-start-date" />
                </div>
                <div style={{ display: "flex", alignItems: "center", gap: "0.5rem" }}>
                  <label style={{ fontWeight: 600, fontSize: "0.9rem" }}>To:</label>
                  <input type="date" className="form-input" value={prodEndDate}
                    onChange={(e) => setProdEndDate(e.target.value)}
                    style={{ width: "auto" }} data-testid="analytics-end-date" />
                </div>
                <button className="btn btn-primary btn-sm" onClick={fetchProductivity} disabled={prodLoading}
                  data-testid="analytics-refresh-btn">
                  {prodLoading ? "Loading..." : "Update"}
                </button>
                <button className="btn btn-secondary btn-sm" onClick={() => {
                  const d = new Date(); d.setDate(1);
                  setProdStartDate(d.toISOString().split("T")[0]);
                  setProdEndDate(new Date().toISOString().split("T")[0]);
                }}>This Month</button>
                <div style={{ marginLeft: "auto" }}>
                  <button className="btn btn-primary btn-sm" onClick={handleDownloadPdf}
                    data-testid="analytics-download-pdf" style={{ display: "flex", alignItems: "center", gap: "0.4rem" }}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4"/><polyline points="7 10 12 15 17 10"/><line x1="12" y1="15" x2="12" y2="3"/></svg>
                    Download PDF Report
                  </button>
                </div>
              </div>
            </div>

            {prodLoading && <p style={{ padding: "1rem", color: "var(--slate-500)" }}>Loading analytics...</p>}

            {productivity && !prodLoading && (<>
              {/* Summary Cards */}
              <div className="stats-grid" style={{ marginBottom: "1.5rem" }}>
                <div className="stat-card">
                  <div className="stat-value">{productivity.summary.total_submissions}</div>
                  <div className="stat-label">Submissions</div>
                </div>
                <div className="stat-card">
                  <div className="stat-value">{productivity.summary.success_rate}%</div>
                  <div className="stat-label">Success Rate</div>
                </div>
                <div className="stat-card">
                  <div className="stat-value">{productivity.summary.avg_processing_hours}h</div>
                  <div className="stat-label">Avg Processing Time</div>
                </div>
                <div className="stat-card">
                  <div className="stat-value">{productivity.summary.total_status_changes}</div>
                  <div className="stat-label">Status Changes</div>
                </div>
                <div className="stat-card">
                  <div className="stat-value">{productivity.summary.total_terminations}</div>
                  <div className="stat-label">Terminations</div>
                </div>
              </div>

              {/* Submissions by Agent */}
              <div className="card" style={{ marginBottom: "1.5rem" }}>
                <div className="card-header"><h2 className="card-title">Submissions by Agent</h2></div>
                <div className="card-body">
                  {productivity.submissions_by_agent.length === 0 ? (
                    <p style={{ color: "var(--slate-500)", padding: "0.5rem 0" }}>No submissions in this period.</p>
                  ) : (
                    <div className="table-container">
                      <table className="data-table">
                        <thead><tr><th>Agent</th><th>Total</th><th>Successful</th><th>Failed</th><th>Success %</th></tr></thead>
                        <tbody>
                          {productivity.submissions_by_agent.map((a, idx) => (
                            <tr key={idx}>
                              <td>{a.agent_name || a._id || "Unknown"}</td>
                              <td>{a.total}</td>
                              <td><span className="badge badge-success">{a.successful}</span></td>
                              <td>{a.failed > 0 ? <span className="badge badge-error">{a.failed}</span> : "—"}</td>
                              <td>{a.total > 0 ? Math.round((a.successful / a.total) * 100) : 0}%</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </div>

              {/* Processing Time by Agent */}
              <div className="card" style={{ marginBottom: "1.5rem" }}>
                <div className="card-header"><h2 className="card-title">Processing Time by Agent</h2></div>
                <div className="card-body">
                  {productivity.processing_by_agent.length === 0 ? (
                    <p style={{ color: "var(--slate-500)", padding: "0.5rem 0" }}>No processing time data in this period.</p>
                  ) : (
                    <div className="table-container">
                      <table className="data-table">
                        <thead><tr><th>Agent</th><th>Records</th><th>Avg (hrs)</th><th>Min (hrs)</th><th>Max (hrs)</th></tr></thead>
                        <tbody>
                          {productivity.processing_by_agent.map((a, idx) => (
                            <tr key={idx}>
                              <td>{a.agent}</td>
                              <td>{a.records_processed}</td>
                              <td>{a.avg_hours}</td>
                              <td>{a.min_hours}</td>
                              <td>{a.max_hours}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </div>

              {/* Status Changes by Agent */}
              <div className="card">
                <div className="card-header"><h2 className="card-title">Status Changes by Agent</h2></div>
                <div className="card-body">
                  {productivity.status_changes_by_agent.length === 0 ? (
                    <p style={{ color: "var(--slate-500)", padding: "0.5rem 0" }}>No status changes in this period.</p>
                  ) : (
                    <div className="table-container">
                      <table className="data-table">
                        <thead><tr><th>Agent</th><th>Total Changes</th><th>Terminations</th></tr></thead>
                        <tbody>
                          {productivity.status_changes_by_agent.map((a, idx) => (
                            <tr key={idx}>
                              <td>{a.admin_name || a._id || "Unknown"}</td>
                              <td>{a.total_changes}</td>
                              <td>{a.terminations || 0}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </div>
            </>)}

            {!productivity && !prodLoading && analytics && (
              <div className="stats-grid">
                <div className="stat-card">
                  <div className="stat-value">{analytics.employees.total}</div>
                  <div className="stat-label">Total Employees</div>
                </div>
                <div className="stat-card">
                  <div className="stat-value">{analytics.employees.processed}</div>
                  <div className="stat-label">Processed</div>
                </div>
                <div className="stat-card">
                  <div className="stat-value">{analytics.employees.unprocessed}</div>
                  <div className="stat-label">Pending</div>
                </div>
                <div className="stat-card">
                  <div className="stat-value">{analytics.submissions.success_rate}%</div>
                  <div className="stat-label">Success Rate</div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Users Tab */}
        {activeTab === "users" && (
          <div className="card">
            <div className="card-header">
              <h2 className="card-title">User Management</h2>
              <button className="btn btn-primary btn-sm" onClick={() => setShowAddUser(true)}>Add User</button>
            </div>
            <div className="card-body">
              <div className="table-container">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Email</th>
                      <th>Name</th>
                      <th>Role</th>
                      <th>Status</th>
                      <th>Last Login</th>
                      <th>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {users.map((u) => (
                      <tr key={u.id}>
                        <td>{u.email}</td>
                        <td>{u.name || "-"}</td>
                        <td>
                          <select
                            value={u.role}
                            onChange={(e) => handleChangeUserRole(u.id, e.target.value)}
                            className="form-select"
                            style={{ width: "auto", padding: "0.25rem 0.5rem" }}
                            disabled={u.id === user.id}
                          >
                            <option value="user">User</option>
                            <option value="admin">Admin</option>
                          </select>
                        </td>
                        <td><span className={`badge ${u.is_active ? "badge-success" : "badge-error"}`}>{u.is_active ? "Active" : "Inactive"}</span></td>
                        <td>{u.last_login ? new Date(u.last_login).toLocaleDateString() : "Never"}</td>
                        <td>
                          {u.id !== user.id && (
                            <>
                              <button className="btn btn-secondary btn-sm" onClick={() => handleToggleUserStatus(u.id, u.is_active)} style={{ marginRight: "0.5rem" }}>
                                {u.is_active ? "Deactivate" : "Activate"}
                              </button>
                              <button className="btn btn-danger btn-sm" onClick={() => handleDeleteUser(u.id)}>Delete</button>
                            </>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        )}

        {/* Unprocessed Employees Tab */}
        {activeTab === "unprocessed" && (
          <div className="card">
            <div className="card-header">
              <h2 className="card-title">Unprocessed Employees</h2>
              <span className="badge badge-info">{unprocessedEmployees.length} pending</span>
            </div>
            <div className="card-body">
              {unprocessedEmployees.length === 0 ? (
                <div className="empty-state"><p>No unprocessed employees. All caught up!</p></div>
              ) : (
                <div className="table-container">
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>Position ID</th>
                        <th>Name</th>
                        <th>Status</th>
                        <th>Facility</th>
                        <th>Created At</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {unprocessedEmployees.map((emp) => (
                        <tr key={emp.id}>
                          <td>{emp.position_id || emp.id}</td>
                          <td>{emp.first_name || "-"} {emp.last_name || "-"}</td>
                          <td><span className="badge badge-warning">{emp.employee_status || "Pending"}</span></td>
                          <td>{emp.home_facility || "-"}</td>
                          <td>{emp.created_at ? new Date(emp.created_at).toLocaleDateString() : "-"}</td>
                          <td>
                            <button 
                              className="btn btn-danger btn-sm" 
                              onClick={() => handleDeleteEmployee(emp.id, `${emp.first_name || ''} ${emp.last_name || ''}`.trim() || emp.position_id || emp.id)}
                              data-testid={`delete-employee-${emp.id}`}
                            >
                              Delete
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
              <div style={{ marginTop: "1rem", padding: "1rem", background: "var(--slate-100)", borderRadius: "8px", fontSize: "0.9rem", color: "var(--slate-600)" }}>
                <strong>Note:</strong> Only unprocessed employees can be deleted. Processed employees must be unprocessed first before deletion.
              </div>
            </div>
          </div>
        )}

        {/* Processed Employees Tab */}
        {activeTab === "processed" && (
          <div className="card">
            <div className="card-header" style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: "1rem", flexWrap: "wrap" }}>
              <h2 className="card-title">Processed Employees</h2>
              <input
                type="text"
                className="form-input"
                placeholder="Search by name, position ID, facility, or processed by..."
                value={processedSearch}
                onChange={(e) => setProcessedSearch(e.target.value)}
                data-testid="processed-search-input"
                style={{ maxWidth: "420px", flex: 1 }}
              />
            </div>
            <div className="card-body">
              {processedEmployees.length === 0 ? (
                <div className="empty-state"><p>No processed employees yet.</p></div>
              ) : (() => {
                const q = processedSearch.trim().toLowerCase();
                const filtered = !q ? processedEmployees : processedEmployees.filter((emp) => {
                  const hay = [
                    emp.first_name, emp.last_name, emp.middle_name,
                    emp.position_id, emp.employee_status,
                    emp.home_facility, emp.facility_id,
                    emp.processed_by, emp.job_title, emp.department,
                    emp.personal_email, emp.adp_code,
                  ].filter(Boolean).join(" ").toLowerCase();
                  return hay.includes(q);
                });
                return (
                  <>
                    <p style={{ fontSize: "0.85rem", color: "var(--slate-500)", margin: "0 0 0.75rem" }}>
                      Showing {filtered.length} of {processedEmployees.length} processed employee{processedEmployees.length === 1 ? "" : "s"}
                    </p>
                    <div className="table-container">
                      <table className="data-table">
                        <thead>
                          <tr>
                            <th>Position ID</th>
                            <th>Name</th>
                            <th>Status</th>
                            <th>Facility</th>
                            <th>Processed By</th>
                            <th>Processed At</th>
                            <th>TherapyQuest</th>
                            <th>Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {filtered.map((emp) => (
                            <tr key={emp.id}>
                              <td>{emp.position_id}</td>
                              <td>{emp.first_name} {emp.last_name}</td>
                              <td><span className="badge badge-info">{emp.employee_status}</span></td>
                              <td>{emp.home_facility || "-"}</td>
                              <td>{emp.processed_by || "-"}</td>
                              <td>{emp.processed_at ? new Date(emp.processed_at).toLocaleDateString() : "-"}</td>
                              <td data-testid={`tq-status-${emp.id}`}>
                                {emp.therapyquest_status === "success" ? (
                                  emp.therapyquest_needs_tmc_account ? (
                                    <span className="badge badge-warning" title="TherapyQuest could not link a TMC account - re-send once the Entra account exists">Sent, no login</span>
                                  ) : (
                                    <span className="badge badge-success" title={emp.therapyquest_sent_at || ""}>Sent</span>
                                  )
                                ) : emp.therapyquest_status === "error" ? (
                                  <span className="badge badge-danger" title={emp.therapyquest_error || ""}>Failed</span>
                                ) : emp.therapyquest_status === "not_configured" ? (
                                  <span className="badge badge-warning" title="THERAPYQUEST_URL is not set">Not configured</span>
                                ) : emp.therapyquest_status === "queued" ? (
                                  <span className="badge badge-info" title="Sending in the background - refresh in a moment">Queued</span>
                                ) : (
                                  <span className="badge badge-info">Not sent</span>
                                )}
                              </td>
                              <td style={{ whiteSpace: "nowrap" }}>
                                <button
                                  className="btn btn-secondary btn-sm"
                                  onClick={() => setViewingProcessed(emp)}
                                  data-testid={`view-processed-${emp.id}`}
                                  style={{ marginRight: "0.4rem" }}
                                >
                                  View
                                </button>
                                <button
                                  className="btn btn-secondary btn-sm"
                                  onClick={() => handleResendTherapyQuest(emp.id)}
                                  disabled={resendingTq === emp.id}
                                  data-testid={`resend-tq-${emp.id}`}
                                  style={{ marginRight: "0.4rem" }}
                                >
                                  {resendingTq === emp.id ? "Sending..." : emp.therapyquest_status === "success" ? "Re-send TQ" : "Send to TQ"}
                                </button>
                                <button className="btn btn-secondary btn-sm" onClick={() => handleUnprocess(emp.id)}>Unprocess</button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </>
                );
              })()}
            </div>
          </div>
        )}

        {/* Dropdown Lists Tab */}
        {activeTab === "lists" && (
          <div style={{ display: "grid", gridTemplateColumns: "280px 1fr", gap: "1.5rem" }}>
            <div className="card">
              <div className="card-header">
                <h2 className="card-title">Lists</h2>
              </div>
              <div className="card-body" style={{ padding: 0 }}>
                {lists.map((list) => (
                  <div
                    key={list.name}
                    onClick={() => setSelectedList(list)}
                    style={{
                      padding: "1rem 1.25rem",
                      cursor: "pointer",
                      borderBottom: "1px solid var(--slate-100)",
                      background: selectedList?.name === list.name ? "var(--primary-50)" : "transparent",
                      color: selectedList?.name === list.name ? "var(--primary-700)" : "inherit",
                    }}
                  >
                    <div style={{ fontWeight: 500 }}>{list.display_name}</div>
                    <div style={{ fontSize: "0.8rem", color: "var(--slate-500)", marginTop: "0.25rem" }}>{list.items.length} items</div>
                  </div>
                ))}
              </div>
            </div>

            <div className="card">
              <div className="card-header">
                <h2 className="card-title">{selectedList?.display_name || "Select a List"}</h2>
                {selectedList && (
                  <div style={{ display: "flex", gap: "0.5rem" }}>
                    <button className="btn btn-secondary btn-sm" onClick={() => setShowBulkAdd(true)}>Bulk Add</button>
                    <button className="btn btn-primary btn-sm" onClick={() => setShowAddItem(true)}>Add Item</button>
                  </div>
                )}
              </div>
              <div className="card-body">
                {!selectedList ? (
                  <div className="empty-state"><p>Select a list from the left to manage its items.</p></div>
                ) : selectedList.items.length === 0 ? (
                  <div className="empty-state"><p>No items in this list yet. Click &quot;Add Item&quot; or &quot;Bulk Add&quot; to get started.</p></div>
                ) : (
                  <div className="list-manager">
                    {isTwoColumnList(selectedList.name) && (
                      <div className="list-header" style={{ display: "grid", gridTemplateColumns: "1fr 1fr auto", gap: "1rem", padding: "0.5rem 1rem", fontWeight: 600, color: "var(--slate-600)", fontSize: "0.85rem", borderBottom: "2px solid var(--slate-200)", marginBottom: "0.5rem" }}>
                        <span>{getListFields(selectedList.name).field1}</span>
                        <span>{getListFields(selectedList.name).field2}</span>
                        <span style={{ width: "80px" }}>Action</span>
                      </div>
                    )}
                    {selectedList.items.map((item, idx) => {
                      const parts = item.split("|");
                      const isTwoCol = isTwoColumnList(selectedList.name) && parts.length >= 2;
                      return (
                        <div key={idx} className="list-item" style={isTwoCol ? { display: "grid", gridTemplateColumns: "1fr 1fr auto", gap: "1rem", alignItems: "center" } : {}}>
                          {isTwoCol ? (
                            <>
                              <span className="list-item-text" style={{ fontWeight: 500 }}>{parts[0]}</span>
                              <span className="list-item-text">{parts[1]}</span>
                            </>
                          ) : (
                            <span className="list-item-text">{item}</span>
                          )}
                          <button className="btn btn-danger btn-sm" onClick={() => handleRemoveListItem(selectedList.name, item)}>Remove</button>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          </div>
        )}






        {/* Entra ID Audit Log Tab */}
        {activeTab === "entraAudit" && (
          <div className="card">
            <div className="card-header">
              <h2 className="card-title">Entra ID Change History</h2>
              <button className="btn btn-secondary btn-sm" onClick={fetchEntraAuditLog}>Refresh</button>
            </div>
            <div className="card-body">
              {entraAuditLog.length === 0 ? (
                <div className="empty-state">
                  <p>No changes have been recorded yet.</p>
                </div>
              ) : (
                <div className="table-container">
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>Date/Time</th>
                        <th>Admin</th>
                        <th>Employee</th>
                        <th>Effective Date</th>
                        <th>Changes Made</th>
                      </tr>
                    </thead>
                    <tbody>
                      {entraAuditLog.map((log, idx) => (
                        <tr key={idx}>
                          <td>{new Date(log.timestamp).toLocaleString()}</td>
                          <td>{log.admin_name || log.admin_email}</td>
                          <td>
                            <div>{log.entra_user_name || log.entra_user_email || log.entra_user_id}</div>
                            {log.entra_user_email && <div style={{ fontSize: "0.8rem", color: "var(--slate-500)" }}>{log.entra_user_email}</div>}
                          </td>
                          <td>{log.effective_date || "—"}</td>
                          <td>
                            <ul style={{ margin: 0, paddingLeft: "1.25rem", fontSize: "0.9rem" }}>
                              {Object.entries(log.changes || {}).filter(([key]) => !["displayName","previousAccountEnabled","previousEmployeeType"].includes(key)).map(([key, value]) => {
                                const labels = {accountEnabled:"Account Status",employeeType:"Employee Type",jobTitle:"Job Title",officeLocation:"Office Location",state:"State",businessPhones:"Business Phone",mobilePhone:"Mobile Phone",personalEmail:"Personal Email",facilityName:"Facility Name",managerEmail:"Manager Email",startDate:"Start Date",licensesRemoved:"Licenses Removed",smartsheetDeactivation:"Smartsheet"};
                                let label = labels[key] || key;
                                let display;
                                if (key === "licensesRemoved" && typeof value === "object" && value !== null) {
                                  const names = value.license_names || [];
                                  if (names.length > 0) {
                                    display = names.join(", ");
                                  } else {
                                    const count = value.removed || 0;
                                    display = count > 0 ? `${count} license(s) removed` : (value.message || "None");
                                  }
                                } else if (key === "smartsheetDeactivation" && typeof value === "object" && value !== null) {
                                  display = value.message || (value.deactivated ? "User deactivated" : "Not found in Smartsheet");
                                } else if (typeof value === "boolean") {
                                  display = value ? "Enabled" : "Disabled";
                                } else if (Array.isArray(value)) {
                                  display = value.join(", ");
                                } else if (typeof value === "object" && value !== null) {
                                  display = JSON.stringify(value);
                                } else {
                                  display = String(value || "(cleared)");
                                }
                                return (
                                  <li key={key}>
                                    <strong>{label}:</strong> {display}
                                  </li>
                                );
                              })}
                            </ul>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Notifications Tab */}
        {activeTab === "notifications" && (
          <div className="card" data-testid="notifications-tab">
            <div className="card-header">
              <h3>Email Notifications</h3>
              <div style={{ display: "flex", gap: "0.5rem" }}>
                <button
                  className="btn btn-secondary"
                  onClick={async () => {
                    try {
                      const result = await notificationsApi.diagnose();
                      setDiagnoseResult(result);
                    } catch (err) {
                      toast.error("Failed to run diagnostics");
                    }
                  }}
                  data-testid="diagnose-notifications-btn"
                  style={{ fontSize: "0.85rem" }}
                >
                  Diagnose
                </button>
                <button
                  className="btn btn-secondary"
                  onClick={handleSendTestEmail}
                  disabled={notifTestLoading}
                  data-testid="send-test-email-btn"
                  style={{ fontSize: "0.85rem" }}
                >
                  {notifTestLoading ? "Sending..." : "Send Test Email to Me"}
                </button>
              </div>
            </div>

            {/* Diagnose Results */}
            {diagnoseResult && (
              <div style={{ margin: "0 1rem 1rem", padding: "1rem", borderRadius: "8px", border: "1px solid " + (diagnoseResult.issues?.length ? "#fecaca" : "#bbf7d0"), background: diagnoseResult.issues?.length ? "#fef2f2" : "#f0fdf4" }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: "0.75rem" }}>
                  <strong style={{ color: diagnoseResult.issues?.length ? "#991b1b" : "#166534" }}>
                    {diagnoseResult.status}
                  </strong>
                  <button onClick={() => setDiagnoseResult(null)} style={{ background: "none", border: "none", cursor: "pointer", color: "#64748b", fontSize: "1rem" }}>X</button>
                </div>
                {diagnoseResult.issues?.length > 0 && (
                  <div style={{ marginBottom: "0.75rem" }}>
                    <strong style={{ fontSize: "0.85rem" }}>Issues:</strong>
                    <ul style={{ margin: "0.25rem 0 0 1.25rem", fontSize: "0.85rem", color: "#991b1b" }}>
                      {diagnoseResult.issues.map((issue, i) => <li key={i}>{issue}</li>)}
                    </ul>
                  </div>
                )}
                <details style={{ fontSize: "0.85rem" }}>
                  <summary style={{ cursor: "pointer", fontWeight: 600, color: "#475569" }}>Full Diagnostic Details</summary>
                  <pre style={{ marginTop: "0.5rem", padding: "0.75rem", background: "white", borderRadius: "6px", border: "1px solid #e2e8f0", overflow: "auto", fontSize: "0.8rem", lineHeight: 1.5 }}>
                    {JSON.stringify(diagnoseResult.checks, null, 2)}
                  </pre>
                </details>
              </div>
            )}

            {notifLoading ? (
              <p style={{ padding: "1rem", color: "var(--slate-500)" }}>Loading notification settings...</p>
            ) : (
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "1.5rem", padding: "1rem" }}>
                {/* Status Change Recipients */}
                <div style={{ border: "1px solid var(--slate-200)", borderRadius: "8px", padding: "1rem" }}>
                  <h4 style={{ margin: "0 0 0.75rem", color: "var(--primary-700)" }}>Status Change Recipients</h4>
                  <p style={{ fontSize: "0.8rem", color: "var(--slate-500)", margin: "0 0 0.75rem" }}>
                    These people are notified when any employee status is changed (excluding terminations).
                  </p>
                  <div style={{ display: "flex", gap: "0.5rem", marginBottom: "0.75rem" }}>
                    <input
                      type="email"
                      className="form-input"
                      placeholder="email@therapymgmt.com"
                      value={notifNewEmail.status_change}
                      onChange={(e) => setNotifNewEmail(prev => ({ ...prev, status_change: e.target.value }))}
                      onKeyDown={(e) => e.key === "Enter" && handleAddNotifEmail("status_change")}
                      style={{ flex: 1 }}
                      data-testid="notif-status-change-email-input"
                    />
                    <button className="btn btn-primary" onClick={() => handleAddNotifEmail("status_change")} style={{ fontSize: "0.85rem" }} data-testid="notif-add-status-change-btn">Add</button>
                  </div>
                  <div style={{ maxHeight: "200px", overflowY: "auto" }}>
                    {notifRecipients.status_change_emails.length === 0 ? (
                      <p style={{ color: "var(--slate-400)", fontStyle: "italic", fontSize: "0.85rem" }}>No recipients configured</p>
                    ) : (
                      notifRecipients.status_change_emails.map((email, idx) => (
                        <div key={idx} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "0.4rem 0", borderBottom: "1px solid var(--slate-100)" }}>
                          <span style={{ fontSize: "0.9rem" }}>{email}</span>
                          <button onClick={() => handleRemoveNotifEmail("status_change", email)} style={{ background: "none", border: "none", color: "var(--red-500)", cursor: "pointer", fontWeight: "bold", fontSize: "1.1rem" }}>×</button>
                        </div>
                      ))
                    )}
                  </div>
                  <button
                    className="btn btn-secondary"
                    onClick={() => handleEditTemplate("status_change")}
                    style={{ marginTop: "0.75rem", fontSize: "0.85rem", width: "100%" }}
                    data-testid="edit-status-change-template-btn"
                  >
                    Edit Email Template
                  </button>
                </div>

                {/* Termination Recipients */}
                <div style={{ border: "1px solid var(--slate-200)", borderRadius: "8px", padding: "1rem" }}>
                  <h4 style={{ margin: "0 0 0.75rem", color: "var(--red-600, #dc2626)" }}>Termination Recipients</h4>
                  <p style={{ fontSize: "0.8rem", color: "var(--slate-500)", margin: "0 0 0.75rem" }}>
                    These people are notified when an employee account is disabled (terminated).
                  </p>
                  <div style={{ display: "flex", gap: "0.5rem", marginBottom: "0.75rem" }}>
                    <input
                      type="email"
                      className="form-input"
                      placeholder="email@therapymgmt.com"
                      value={notifNewEmail.termination}
                      onChange={(e) => setNotifNewEmail(prev => ({ ...prev, termination: e.target.value }))}
                      onKeyDown={(e) => e.key === "Enter" && handleAddNotifEmail("termination")}
                      style={{ flex: 1 }}
                      data-testid="notif-termination-email-input"
                    />
                    <button className="btn btn-primary" onClick={() => handleAddNotifEmail("termination")} style={{ fontSize: "0.85rem" }} data-testid="notif-add-termination-btn">Add</button>
                  </div>
                  <div style={{ maxHeight: "200px", overflowY: "auto" }}>
                    {notifRecipients.termination_emails.length === 0 ? (
                      <p style={{ color: "var(--slate-400)", fontStyle: "italic", fontSize: "0.85rem" }}>No recipients configured</p>
                    ) : (
                      notifRecipients.termination_emails.map((email, idx) => (
                        <div key={idx} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "0.4rem 0", borderBottom: "1px solid var(--slate-100)" }}>
                          <span style={{ fontSize: "0.9rem" }}>{email}</span>
                          <button onClick={() => handleRemoveNotifEmail("termination", email)} style={{ background: "none", border: "none", color: "var(--red-500)", cursor: "pointer", fontWeight: "bold", fontSize: "1.1rem" }}>×</button>
                        </div>
                      ))
                    )}
                  </div>
                  <button
                    className="btn btn-secondary"
                    onClick={() => handleEditTemplate("termination")}
                    style={{ marginTop: "0.75rem", fontSize: "0.85rem", width: "100%" }}
                    data-testid="edit-termination-template-btn"
                  >
                    Edit Email Template
                  </button>
                </div>
              </div>
            )}

            {/* Template variables help */}
            <div style={{ padding: "0 1rem 1rem", fontSize: "0.8rem", color: "var(--slate-500)" }}>
              <strong>Available template variables:</strong>{" "}
              <code>{"{{employeeName}}"}</code>, <code>{"{{modifiedBy}}"}</code>, <code>{"{{changeDate}}"}</code>, <code>{"{{changes}}"}</code>, <code>{"{{employeeEmail}}"}</code>, <code>{"{{employeeId}}"}</code>, <code>{"{{jobTitle}}"}</code>, <code>{"{{department}}"}</code>
            </div>

            {/* Template Editor Modal */}
            {notifEditingTemplate && (
              <div style={{
                position: "fixed", top: 0, left: 0, right: 0, bottom: 0,
                background: "rgba(0,0,0,0.5)", display: "flex", alignItems: "center", justifyContent: "center", zIndex: 1000
              }} onClick={() => setNotifEditingTemplate(null)}>
                <div style={{
                  background: "white", borderRadius: "10px", width: "90%", maxWidth: "800px", maxHeight: "85vh",
                  display: "flex", flexDirection: "column", overflow: "hidden"
                }} onClick={(e) => e.stopPropagation()}>
                  <div style={{ padding: "1rem 1.5rem", borderBottom: "1px solid var(--slate-200)", display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <h3 style={{ margin: 0 }}>
                      Edit {notifEditingTemplate === "termination" ? "Termination" : "Status Change"} Email Template
                    </h3>
                    <button onClick={() => setNotifEditingTemplate(null)} style={{ background: "none", border: "none", fontSize: "1.5rem", cursor: "pointer", color: "var(--slate-500)" }}>×</button>
                  </div>
                  <div style={{ padding: "1rem 1.5rem", overflowY: "auto", flex: 1 }}>
                    <div className="form-group">
                      <label className="form-label">Subject Line</label>
                      <input
                        type="text"
                        className="form-input"
                        value={notifTemplateEdit.subject}
                        onChange={(e) => setNotifTemplateEdit(prev => ({ ...prev, subject: e.target.value }))}
                        placeholder="Email subject..."
                        data-testid="template-subject-input"
                      />
                    </div>
                    <div className="form-group">
                      <label className="form-label">HTML Body</label>
                      <textarea
                        className="form-input"
                        value={notifTemplateEdit.html_body}
                        onChange={(e) => setNotifTemplateEdit(prev => ({ ...prev, html_body: e.target.value }))}
                        placeholder="<h2>Notification</h2><p>...</p>"
                        style={{ minHeight: "250px", fontFamily: "monospace", fontSize: "0.85rem" }}
                        data-testid="template-html-body-input"
                      />
                    </div>
                    <div>
                      <label className="form-label">Preview</label>
                      <div
                        style={{ border: "1px solid var(--slate-300)", borderRadius: "5px", padding: "1rem", background: "white", minHeight: "100px" }}
                        dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(notifTemplateEdit.html_body
                          .replace(/\{\{employeeName\}\}/g, "John Doe")
                          .replace(/\{\{modifiedBy\}\}/g, user?.email || "admin@therapymgmt.com")
                          .replace(/\{\{changeDate\}\}/g, new Date().toLocaleString())
                          .replace(/\{\{changes\}\}/g, "<table style='border-collapse:collapse;width:100%;'><tr><td style='padding:6px 12px;border:1px solid #ddd;font-weight:bold;'>Status</td><td style='padding:6px 12px;border:1px solid #ddd;'>Disabled</td></tr></table>")
                          .replace(/\{\{employeeEmail\}\}/g, "jdoe@therapymgmt.com")
                          .replace(/\{\{employeeId\}\}/g, "EMP001")
                          .replace(/\{\{jobTitle\}\}/g, "Therapist")
                          .replace(/\{\{department\}\}/g, "Rehab")
                        ) }}
                      />
                    </div>
                  </div>
                  <div style={{ padding: "1rem 1.5rem", borderTop: "1px solid var(--slate-200)", display: "flex", justifyContent: "flex-end", gap: "0.75rem" }}>
                    <button className="btn btn-secondary" onClick={() => setNotifEditingTemplate(null)}>Cancel</button>
                    <button className="btn btn-primary" onClick={handleSaveTemplate} data-testid="save-template-btn">Save Template</button>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {activeTab === "whatsNew" && (
          <div className="card">
            <div className="card-header" style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
              <h2 className="card-title">What&apos;s New — Announcements</h2>
              <button
                className="btn btn-primary btn-sm"
                onClick={handleNewAnnouncement}
                data-testid="whats-new-add-btn"
              >
                + New Announcement
              </button>
            </div>
            <div className="card-body">
              <p style={{ fontSize: "0.85rem", color: "var(--slate-500)", marginTop: 0 }}>
                Announcements appear in the bell icon at the top-right of the Process Employees screen for all portal users.
                Bodies support <strong>Markdown</strong> (headings, lists, links, bold/italic, code).
              </p>

              {/* Editor */}
              <div
                style={{
                  border: "1px solid var(--slate-200)",
                  borderRadius: "8px",
                  padding: "1rem",
                  marginBottom: "1.25rem",
                  background: "var(--slate-50)",
                }}
                data-testid="whats-new-editor"
              >
                <strong style={{ display: "block", marginBottom: "0.75rem", color: "var(--slate-700)" }}>
                  {editingAnn ? `Editing: ${editingAnn.title}` : "Create new announcement"}
                </strong>
                <div className="form-group">
                  <label className="form-label">Title</label>
                  <input
                    type="text"
                    className="form-input"
                    value={annDraft.title}
                    onChange={(e) => setAnnDraft({ ...annDraft, title: e.target.value })}
                    placeholder="e.g., New: Bulk export of processed employees"
                    data-testid="whats-new-title-input"
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Body (Markdown)</label>
                  <textarea
                    className="form-input"
                    rows={8}
                    value={annDraft.body}
                    onChange={(e) => setAnnDraft({ ...annDraft, body: e.target.value })}
                    placeholder={"Use **bold**, *italic*, [link](https://...), or bullet lists:\n\n- First point\n- Second point"}
                    style={{ resize: "vertical", fontFamily: "ui-monospace, SFMono-Regular, Menlo, monospace", fontSize: "0.9rem" }}
                    data-testid="whats-new-body-input"
                  />
                </div>
                <div style={{ display: "flex", gap: "0.75rem", alignItems: "center", marginTop: "0.5rem" }}>
                  <label style={{ display: "inline-flex", alignItems: "center", gap: "0.4rem", fontSize: "0.9rem" }}>
                    <input
                      type="checkbox"
                      checked={annDraft.active}
                      onChange={(e) => setAnnDraft({ ...annDraft, active: e.target.checked })}
                      data-testid="whats-new-active-checkbox"
                    />
                    Active (visible to users)
                  </label>
                  <div style={{ marginLeft: "auto", display: "flex", gap: "0.5rem" }}>
                    {editingAnn && (
                      <button
                        className="btn btn-secondary btn-sm"
                        onClick={() => { setEditingAnn(null); setAnnDraft({ title: "", body: "", active: true }); }}
                      >
                        Cancel
                      </button>
                    )}
                    <button
                      className="btn btn-primary btn-sm"
                      onClick={handleSaveAnnouncement}
                      disabled={annSaving}
                      data-testid="whats-new-save-btn"
                    >
                      {annSaving ? "Saving…" : editingAnn ? "Save Changes" : "Publish"}
                    </button>
                  </div>
                </div>
              </div>

              {/* List */}
              {announcements.length === 0 ? (
                <div className="empty-state"><p>No announcements yet. Publish the first one above.</p></div>
              ) : (
                <div className="table-container">
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>Title</th>
                        <th>Published</th>
                        <th>Author</th>
                        <th>Status</th>
                        <th style={{ width: "180px" }}>Actions</th>
                      </tr>
                    </thead>
                    <tbody>
                      {announcements.map((a) => (
                        <tr key={a.id}>
                          <td style={{ fontWeight: 600 }}>{a.title}</td>
                          <td>{a.published_at ? new Date(a.published_at).toLocaleString() : "-"}</td>
                          <td>{a.published_by_name || a.published_by || "-"}</td>
                          <td>
                            <span className={`badge ${a.active ? "badge-success" : "badge-secondary"}`}>
                              {a.active ? "Active" : "Inactive"}
                            </span>
                          </td>
                          <td style={{ whiteSpace: "nowrap" }}>
                            <button
                              className="btn btn-secondary btn-sm"
                              onClick={() => handleEditAnnouncement(a)}
                              data-testid={`whats-new-edit-${a.id}`}
                              style={{ marginRight: "0.4rem" }}
                            >
                              Edit
                            </button>
                            {a.active ? (
                              <button
                                className="btn btn-secondary btn-sm"
                                onClick={() => handleDeactivateAnnouncement(a)}
                                data-testid={`whats-new-deactivate-${a.id}`}
                              >
                                Deactivate
                              </button>
                            ) : (
                              <button
                                className="btn btn-secondary btn-sm"
                                onClick={() => handleReactivateAnnouncement(a)}
                                data-testid={`whats-new-reactivate-${a.id}`}
                              >
                                Reactivate
                              </button>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}
      </main>

      {/* Add User Modal */}
      {showAddUser && (
        <div className="modal-overlay" onClick={() => setShowAddUser(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3 className="modal-title">Add New User</h3>
              <button className="modal-close" onClick={() => setShowAddUser(false)}>×</button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label className="form-label">Email Address</label>
                <input type="email" className="form-input" value={newUserEmail} onChange={(e) => setNewUserEmail(e.target.value)} placeholder="user@therapymgmt.com" />
              </div>
              <div className="form-group">
                <label className="form-label">Role</label>
                <select className="form-select" value={newUserRole} onChange={(e) => setNewUserRole(e.target.value)}>
                  <option value="user">User</option>
                  <option value="admin">Admin</option>
                </select>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setShowAddUser(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={handleAddUser}>Add User</button>
            </div>
          </div>
        </div>
      )}

      {/* Add Single Item Modal */}
      {showAddItem && (
        <div className="modal-overlay" onClick={() => setShowAddItem(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h3 className="modal-title">Add Item to {selectedList?.display_name}</h3>
              <button className="modal-close" onClick={() => setShowAddItem(false)}>×</button>
            </div>
            <div className="modal-body">
              {isTwoColumnList(selectedList?.name) ? (
                <>
                  <div className="form-group">
                    <label className="form-label">{getListFields(selectedList?.name).field1}</label>
                    <input type="text" className="form-input" value={newItemField1} onChange={(e) => setNewItemField1(e.target.value)} placeholder={`Enter ${getListFields(selectedList?.name).field1}`} />
                  </div>
                  <div className="form-group">
                    <label className="form-label">{getListFields(selectedList?.name).field2}</label>
                    <input type="text" className="form-input" value={newItemField2} onChange={(e) => setNewItemField2(e.target.value)} placeholder={`Enter ${getListFields(selectedList?.name).field2}`} />
                  </div>
                </>
              ) : (
                <div className="form-group">
                  <label className="form-label">Item Name</label>
                  <input type="text" className="form-input" value={newItem} onChange={(e) => setNewItem(e.target.value)} placeholder="Enter item name" />
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setShowAddItem(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={handleAddListItem}>Add Item</button>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Add Modal */}
      {showBulkAdd && (
        <div className="modal-overlay" onClick={() => setShowBulkAdd(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: "600px" }}>
            <div className="modal-header">
              <h3 className="modal-title">Bulk Add to {selectedList?.display_name}</h3>
              <button className="modal-close" onClick={() => setShowBulkAdd(false)}>×</button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                {isTwoColumnList(selectedList?.name) ? (
                  <>
                    <label className="form-label">Paste items (one per line: {getListFields(selectedList?.name).field1} | {getListFields(selectedList?.name).field2})</label>
                    <textarea
                      className="form-input"
                      value={bulkItems}
                      onChange={(e) => setBulkItems(e.target.value)}
                      placeholder={`FAC001|Main Hospital\nFAC002|North Clinic\nFAC003|South Clinic\n\nOr use tabs/commas as separators`}
                      rows={10}
                      style={{ resize: "vertical", fontFamily: "monospace" }}
                    />
                    <div className="form-hint">
                      Each line should have two values separated by | (pipe), tab, or comma. Example: FAC001|Main Hospital
                    </div>
                  </>
                ) : (
                  <>
                    <label className="form-label">Paste items (one per line, or comma/tab separated)</label>
                    <textarea
                      className="form-input"
                      value={bulkItems}
                      onChange={(e) => setBulkItems(e.target.value)}
                      placeholder="Item 1&#10;Item 2&#10;Item 3&#10;&#10;Or paste comma-separated: Item 1, Item 2, Item 3"
                      rows={10}
                      style={{ resize: "vertical", fontFamily: "monospace" }}
                    />
                    <div className="form-hint">
                      Paste from Excel or CSV. Items can be separated by new lines, commas, or tabs. Duplicates will be skipped.
                    </div>
                  </>
                )}
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn btn-secondary" onClick={() => setShowBulkAdd(false)}>Cancel</button>
              <button className="btn btn-primary" onClick={handleBulkAddItems}>Add All Items</button>
            </div>
          </div>
        </div>
      )}

      {/* Processed Employee Detail Modal — read-only view of every field */}
      {viewingProcessed && (() => {
        // Group fields so the modal is organized and skim-friendly.
        // Hidden fields are internal/derived (id, created_at, raw flags). Everything else shows.
        const HIDDEN_KEYS = new Set([
          "id", "_id",
          "submission_locked", "entra_check_performed",
        ]);
        const HUMANIZE = (k) => k
          .replace(/_/g, " ")
          .replace(/\b\w/g, (c) => c.toUpperCase())
          .replace(/Adp/g, "ADP")
          .replace(/Tcn/g, "TCN")
          .replace(/Hr /g, "HR ")
          .replace(/Id$/, "ID")
          .replace(/Id /g, "ID ")
          .replace(/Ops/g, "Ops");

        const sections = [
          { title: "Identity", keys: ["position_id", "first_name", "middle_name", "last_name", "employee_status", "is_a_tcn", "discipline", "designation"] },
          { title: "Contact", keys: ["personal_email", "work_email", "tmc_username", "home_phone_number", "personal_cell_number", "birth_date"] },
          { title: "Position", keys: ["position_start_date", "pay_group", "job_title", "employee_type", "roles", "ops_admin", "clock_in_out", "adp_code", "hr_agent"] },
          { title: "Facility", keys: ["home_facility", "facility_id", "additional_facilities"] },
          { title: "License", keys: ["license_state", "license_number", "license_issue_date", "license_expiration"] },
          { title: "Notifications", keys: ["manager_email", "director_of_rehabilitation_email", "facility_email"] },
          { title: "Custom / Notes", keys: ["custom_field_1", "custom_field_2", "notes"] },
          { title: "Processing", keys: ["processed", "processed_at", "processed_by", "submitted_at", "submission_status", "submission_error", "entra_original_first_name", "created_at", "updated_at"] },
          { title: "TherapyQuest", keys: ["therapyquest_status", "therapyquest_sent_at", "therapyquest_attempts", "therapyquest_last_trigger", "therapyquest_needs_tmc_account", "therapyquest_url_used", "therapyquest_error", "therapyquest_response"] },
        ];
        const seen = new Set(["id", "_id"]);
        sections.forEach((s) => s.keys.forEach((k) => seen.add(k)));
        // Anything we forgot to group — append to the bottom so nothing hides
        const extraKeys = Object.keys(viewingProcessed).filter((k) => !seen.has(k) && !HIDDEN_KEYS.has(k));
        if (extraKeys.length) sections.push({ title: "Other", keys: extraKeys });

        const renderValue = (v) => {
          if (v === null || v === undefined || v === "") return <span style={{ color: "var(--slate-400)" }}>—</span>;
          if (typeof v === "boolean") return v ? "Yes" : "No";
          if (typeof v === "string" && /^\d{4}-\d{2}-\d{2}T/.test(v)) {
            try { return new Date(v).toLocaleString(); } catch { return v; }
          }
          return String(v);
        };

        return (
          <div className="modal-overlay" onClick={() => setViewingProcessed(null)} data-testid="view-processed-modal">
            <div className="modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: "860px", maxHeight: "90vh", display: "flex", flexDirection: "column" }}>
              <div className="modal-header">
                <h3 className="modal-title">
                  {viewingProcessed.first_name} {viewingProcessed.last_name}
                  <span style={{ fontSize: "0.85rem", color: "var(--slate-500)", marginLeft: "0.75rem", fontWeight: 400 }}>
                    Position {viewingProcessed.position_id || "—"}
                  </span>
                </h3>
                <button className="modal-close" onClick={() => setViewingProcessed(null)}>×</button>
              </div>
              <div className="modal-body" style={{ overflow: "auto", flex: 1 }}>
                {sections.map((section) => {
                  const rows = section.keys
                    .filter((k) => k in viewingProcessed && !HIDDEN_KEYS.has(k))
                    .map((k) => ({ k, v: viewingProcessed[k] }));
                  if (!rows.length) return null;
                  return (
                    <div key={section.title} style={{ marginBottom: "1.25rem" }}>
                      <h4 style={{
                        margin: "0 0 0.5rem", fontSize: "0.78rem", fontWeight: 700,
                        textTransform: "uppercase", letterSpacing: "0.04em", color: "var(--slate-500)",
                        borderBottom: "1px solid var(--slate-200)", paddingBottom: "0.3rem"
                      }}>
                        {section.title}
                      </h4>
                      <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "0.9rem" }}>
                        <tbody>
                          {rows.map(({ k, v }) => (
                            <tr key={k}>
                              <td style={{ padding: "0.35rem 0.6rem", fontWeight: 600, color: "var(--slate-700)", verticalAlign: "top", width: "240px" }}>
                                {HUMANIZE(k)}
                              </td>
                              <td style={{ padding: "0.35rem 0.6rem", color: "var(--slate-900)", wordBreak: "break-word" }}>
                                {renderValue(v)}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  );
                })}
              </div>
              <div className="modal-footer">
                <button
                  className="btn btn-secondary"
                  onClick={() => {
                    // Copy the full record to clipboard as JSON for forwarding/debugging
                    try {
                      navigator.clipboard.writeText(JSON.stringify(viewingProcessed, null, 2));
                      toast.success("Employee record copied to clipboard as JSON");
                    } catch {
                      toast.error("Copy failed — browser blocked clipboard access");
                    }
                  }}
                  data-testid="copy-processed-json"
                >
                  Copy as JSON
                </button>
                <button className="btn btn-primary" onClick={() => setViewingProcessed(null)}>Close</button>
              </div>
            </div>
          </div>
        );
      })()}
    </div>
  );
};

export default AdminPage;
